import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../api';
import KpiCard from '../components/KpiCard';

const LANG_COLORS = [
  '#00d4ff', '#34d399', '#fbbf24', '#f87171', '#a78bfa',
  '#fb7185', '#60a5fa', '#facc15', '#4ade80', '#f472b6',
  '#22d3ee', '#fdba74', '#c084fc', '#86efac',
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
}
function fmtMs(ms) {
  if (!ms || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
function fmtShortDate(iso) {
  return iso ? iso.slice(5) : ''; // MM-DD
}

export default function PushStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    setErr('');
    api.pushStats.summary(days)
      .then(setData)
      .catch(e => setErr(e.message || 'Hiba'))
      .finally(() => setLoading(false));
  }, [days]);

  const dailyData = (data?.dailyTimeline || []).map(d => ({
    date: fmtShortDate(d.date),
    sent: d.sent,
    skipped: d.skipped,
  }));

  const langData = data ? Object.entries(data.byLang)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value })) : [];

  const unlistenedData = data ? Object.entries(data.byUnlistened)
    .map(([name, value]) => ({ name, value })) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#fff' }}>🔔 Push Performance</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid #2a2e3a',
                background: days === d ? 'rgba(0,212,255,0.15)' : 'transparent',
                color: days === d ? '#00d4ff' : '#8b8fa3',
                fontWeight: days === d ? 600 : 400, cursor: 'pointer', fontSize: 13,
              }}>
              {d} nap
            </button>
          ))}
        </div>
      </div>

      {err && <div style={{ padding: 12, background: 'rgba(248,113,113,0.1)', border: '1px solid #f87171', borderRadius: 8, color: '#f87171', marginBottom: 16 }}>{err}</div>}

      {loading && <div style={{ color: '#8b8fa3' }}>Betöltés...</div>}

      {!loading && data && data.totals.runs === 0 && (
        <div style={{ padding: 24, background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#8b8fa3' }}>
          Nincs még push_stats adat ebben az időszakban. Az első run után jelenik meg adat.
        </div>
      )}

      {!loading && data && data.totals.runs > 0 && (
        <>
          {/* KPI cards */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <KpiCard label={`Összes push (${days} nap)`} value={data.totals.sent} />
            <KpiCard label="Átlag / nap" value={Math.round(data.avgPerDay)} />
            <KpiCard label="Összes lekért user" value={data.totals.queried} color="#8b8fa3" />
            <KpiCard label="Success rate" value={data.successRate} suffix="%" />
            <KpiCard label="Átlag futási idő" value={fmtMs(data.avgDurationMs)} color="#8b8fa3" />
            <KpiCard label="Hibás futások" value={data.totals.errors} color={data.totals.errors > 0 ? '#f87171' : '#34d399'} />
          </div>

          {/* Daily bar chart */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Napi pushok (utolsó {days} nap)</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
                <XAxis dataKey="date" stroke="#8b8fa3" tick={{ fontSize: 11 }} />
                <YAxis stroke="#8b8fa3" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent" fill="#00d4ff" name="Elküldve" />
                <Bar dataKey="skipped" fill="#475569" name="Kihagyva" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Two-column: lang donut + unlistened buckets */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, marginBottom: 20 }}>
            <div className="card">
              <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Nyelvi eloszlás</div>
              {langData.length === 0 ? (
                <div style={{ color: '#8b8fa3', padding: 20 }}>Nincs adat</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={langData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {langData.map((_, i) => (
                        <Cell key={i} fill={LANG_COLORS[i % LANG_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 6 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card">
              <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Unlistened bucketek</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={unlistenedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
                  <XAxis dataKey="name" stroke="#8b8fa3" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#8b8fa3" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#34d399" name="Userek" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent runs table */}
          <div className="card">
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Utolsó 7 futás</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2a2e3a', color: '#8b8fa3' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Dátum</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Lekérve</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Elküldve</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Kihagyva</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Idő</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center' }}>Top nyelv</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Hiba</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentRuns.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #2a2e3a', color: '#fff' }}>
                      <td style={{ padding: '8px 10px' }}>{fmtDateTime(r.date)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#8b8fa3' }}>{r.totalQueried}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#00d4ff', fontWeight: 600 }}>{r.sent}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#8b8fa3' }}>{r.skipped}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#8b8fa3' }}>{fmtMs(r.durationMs)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{r.topLang || '—'}</td>
                      <td style={{ padding: '8px 10px', color: r.error ? '#f87171' : '#34d399' }}>{r.error || 'ok'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
